import { useEffect, useState } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
  LexicalEditor,
} from 'lexical';
import { $isHeadingNode } from '@lexical/rich-text';
import { $isListNode, ListNode } from '@lexical/list';

export interface ToolbarState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isCode: boolean;
  blockType: string;
}

export function useToolbarState(editor: LexicalEditor): ToolbarState {
  const [state, setState] = useState<ToolbarState>({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isCode: false,
    blockType: 'paragraph',
  });

  useEffect(() => {
    if (!editor) return;

    const update = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchorNode = selection.anchor.getNode();
        const parent = anchorNode.getParent();

        let blockType = 'paragraph';

        if (parent && $isHeadingNode(parent)) {
          blockType = parent.getTag();
        } else if (parent && $isListNode(parent)) {
          blockType = (parent as ListNode).getListType();
        }

        setState({
          isBold: selection.hasFormat('bold'),
          isItalic: selection.hasFormat('italic'),
          isUnderline: selection.hasFormat('underline'),
          isCode: selection.hasFormat('code'),
          blockType,
        });
      });
    };

    const removeUpdateListener = editor.registerUpdateListener(() => {
      update();
    });

    const removeSelectionListener = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        update();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    update();

    return () => {
      removeUpdateListener();
      removeSelectionListener();
    };
  }, [editor]);

  return state;
}
